import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Search, X, ChevronDown, ChevronRight, Package, Grid, Clock, History } from 'lucide-react';
import axios from '../lib/axios';
import { useLanguage, API } from '../App';
import { getIconComponent } from '../utils/categoryIcons';
import { usePersistedCategory, usePersistedState } from '../hooks/usePersistedState';
import { safeClick } from '../utils/safeClick';
import { getSubcategoryTranslation } from '../i18n-subcategories';

/**
 * Reusable Search Bar Component
 * Used in Navbar bottom row for desktop/tablet
 */
const SearchBar = ({ height = 'h-12', className = '' }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { language, t } = useLanguage();
  
  // Category state
  const [categories, setCategories] = useState([]);
  const { 
    category: selectedCategory, 
    setCategory: setSelectedCategory, 
    subcategory: selectedSubcategory, 
    setSubcategory: setSelectedSubcategory 
  } = usePersistedCategory();
  
  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
  const [showSubcategoryDropdown, setShowSubcategoryDropdown] = useState(false);
  const [searchFocused, setSearchFocused] = useState(false);
  const [recentSearches, setRecentSearches] = usePersistedState('recentSearches', []);
  
  // Listen for recentSearchesCleared event from other components
  useEffect(() => {
    const handleClear = () => {
      setRecentSearches([]);
    };
    window.addEventListener('recentSearchesCleared', handleClear);
    return () => window.removeEventListener('recentSearchesCleared', handleClear);
  }, [setRecentSearches]);
  
  // Autocomplete state
  const [suggestions, setSuggestions] = useState([]);
  const [suggestedCategories, setSuggestedCategories] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [suggestionsLoading, setSuggestionsLoading] = useState(false);
  const [selectedSuggestionIndex, setSelectedSuggestionIndex] = useState(-1);
  
  // Refs
  const searchInputRef = useRef(null);
  const searchBarRef = useRef(null);
  const suggestionsRef = useRef(null);
  const debounceTimerRef = useRef(null);
  const isTypingRef = useRef(false); // Track if user is actively typing vs URL sync

  // Get active category info
  const activeCategoryInfo = useMemo(() => {
    if (!selectedCategory || !categories.length) return null;
    return categories.find(c => c.id === selectedCategory);
  }, [selectedCategory, categories]);

  // Fetch categories on mount
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await axios.get(`${API}/categories`);
        setCategories(response.data || []);
      } catch (error) {
        console.error('Failed to fetch categories:', error);
      }
    };
    fetchCategories();
  }, []);

  // Sync search query with URL
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const urlSearch = params.get('search') || params.get('q') || '';
    if (urlSearch !== searchQuery) {
      setSearchQuery(urlSearch);
    }
  }, [location.search]);

  // Close dropdowns on click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchBarRef.current && !searchBarRef.current.contains(event.target)) {
        setShowCategoryDropdown(false);
        setShowSubcategoryDropdown(false);
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Fetch autocomplete suggestions - only when user is actively typing
  useEffect(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Don't fetch or show suggestions if not actively typing (e.g., URL sync)
    if (!isTypingRef.current) {
      return;
    }

    if (searchQuery.length < 2) {
      setSuggestions([]);
      setSuggestedCategories([]);
      setShowSuggestions(false);
      return;
    }

    debounceTimerRef.current = setTimeout(async () => {
      setSuggestionsLoading(true);
      try {
        const params = new URLSearchParams({ q: searchQuery, limit: '5' });
        if (selectedCategory) params.append('category', selectedCategory);
        
        const response = await axios.get(`${API}/listings/suggest?${params}`);
        setSuggestions(response.data.suggestions || []);
        setSuggestedCategories(response.data.category_suggestions || []);
        setShowSuggestions(true);
      } catch (error) {
        console.error('Autocomplete error:', error);
      } finally {
        setSuggestionsLoading(false);
      }
    }, 300);

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [searchQuery, selectedCategory]);

  // Handle search submit
  const handleSearch = () => {
    const params = new URLSearchParams();
    if (searchQuery.trim()) {
      params.set('search', searchQuery.trim());
      // Save to recent searches
      const updated = [searchQuery.trim(), ...recentSearches.filter(s => s !== searchQuery.trim())].slice(0, 5);
      setRecentSearches(updated);
    }
    if (selectedCategory) params.set('category', selectedCategory);
    if (selectedSubcategory) params.set('subcategory', selectedSubcategory);
    
    // Stop typing mode and hide suggestions
    isTypingRef.current = false;
    setShowSuggestions(false);
    setSearchFocused(false);
    searchInputRef.current?.blur();
    
    navigate(`/marketplace?${params.toString()}`);
  };

  // Handle keyboard navigation
  const handleSearchKeyDown = (e) => {
    const totalItems = suggestions.length + suggestedCategories.length;
    
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedSuggestionIndex(prev => (prev < totalItems - 1 ? prev + 1 : 0));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedSuggestionIndex(prev => (prev > 0 ? prev - 1 : totalItems - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (selectedSuggestionIndex >= 0 && selectedSuggestionIndex < suggestions.length) {
        handleSuggestionClick(suggestions[selectedSuggestionIndex]);
      } else if (selectedSuggestionIndex >= suggestions.length) {
        const catIndex = selectedSuggestionIndex - suggestions.length;
        handleCategorySuggestionClick(suggestedCategories[catIndex].id);
      } else {
        handleSearch();
      }
    } else if (e.key === 'Escape') {
      setShowSuggestions(false);
      searchInputRef.current?.blur();
    }
  };

  const handleSuggestionClick = (suggestion) => {
    navigate(`/listing/${suggestion.slug || suggestion.id}`);
    setShowSuggestions(false);
  };

  const handleCategorySuggestionClick = (categoryId) => {
    setSelectedCategory(categoryId);
    const params = new URLSearchParams();
    params.set('category', categoryId);
    params.set('search', searchQuery);
    navigate(`/marketplace?${params.toString()}`);
    setShowSuggestions(false);
  };

  const clearRecentSearches = () => {
    setRecentSearches([]);
    localStorage.removeItem('recentSearches');
    // Dispatch event so other components (like PersonalizedRecommendations) can sync
    window.dispatchEvent(new CustomEvent('recentSearchesCleared'));
  };

  const highlightMatch = (text, query) => {
    if (!query) return text;
    const parts = text.split(new RegExp(`(${query})`, 'gi'));
    return parts.map((part, i) => 
      part.toLowerCase() === query.toLowerCase() 
        ? <mark key={i} className="bg-primary/20 text-primary">{part}</mark>
        : part
    );
  };

  return (
    <div 
      ref={searchBarRef} 
      className={`flex items-center ${height} bg-secondary/80 ring-1 ring-border rounded-full relative z-[60] transition-colors duration-300 focus-within:ring-primary/60 ${className}`}
    >
      {/* Category Dropdown */}
      <div className="relative z-30">
        <button
          {...safeClick(() => {
            setShowCategoryDropdown(!showCategoryDropdown);
            setShowSubcategoryDropdown(false);
          })}
          className={`flex items-center gap-2 ${height} px-4 min-w-[100px] lg:min-w-[160px] text-sm font-medium text-foreground bg-transparent hover:bg-secondary/50 transition-colors rounded-l-full border-r border-border/40 touch-manipulation`}
        >
          {selectedCategory ? (
            activeCategoryInfo ? (
              (() => {
                const ActiveCatIcon = getIconComponent(activeCategoryInfo.icon);
                return (
                  <>
                    <ActiveCatIcon className="w-4 h-4" />
                    <span className="hidden lg:inline max-w-[100px] truncate">{activeCategoryInfo.name}</span>
                  </>
                );
              })()
            ) : (
              <>
                <Package className="w-4 h-4" />
                <span className="hidden lg:inline max-w-[100px] truncate capitalize">{selectedCategory.replace(/-/g, ' ')}</span>
              </>
            )
          ) : (
            <>
              <Grid className="w-4 h-4 text-muted-foreground" />
              <span className="hidden lg:inline">{t('all_categories')}</span>
              <span className="lg:hidden">{t('all')}</span>
            </>
          )}
          <ChevronDown className={`w-3.5 h-3.5 text-muted-foreground transition-transform ${showCategoryDropdown ? 'rotate-180' : ''}`} />
        </button>
        
        {showCategoryDropdown && (
          <div className="absolute top-full left-0 mt-2 w-56 bg-card border border-border rounded-xl shadow-xl z-[70] py-2 max-h-80 overflow-y-auto">
            <button
              onClick={() => {
                setSelectedCategory('');
                setSelectedSubcategory('');
                setShowCategoryDropdown(false);
                navigate('/categories');
              }}
              className={`w-full px-4 py-2.5 text-left text-sm flex items-center gap-3 transition-colors ${
                !selectedCategory ? 'bg-primary/10 text-primary' : 'text-foreground hover:bg-secondary'
              }`}
            >
              <Grid className="w-4 h-4" />
              <span>{t('all_categories')}</span>
            </button>
            {categories.map((cat) => {
              const CatIcon = getIconComponent(cat.icon);
              return (
                <button
                  key={cat.id}
                  onClick={() => {
                    setSelectedCategory(cat.id);
                    setSelectedSubcategory('');
                    setShowCategoryDropdown(false);
                    const params = new URLSearchParams();
                    params.set('category', cat.id);
                    if (searchQuery.trim()) params.set('search', searchQuery.trim());
                    navigate(`/marketplace?${params.toString()}`);
                  }}
                  className={`w-full px-4 py-2.5 text-left text-sm flex items-center gap-3 transition-colors ${
                    selectedCategory === cat.id ? 'bg-primary/10 text-primary' : 'text-foreground hover:bg-secondary'
                  }`}
                >
                  <CatIcon className="w-4 h-4" />
                  <span>{cat.name}</span>
                </button>
              );
            })}
          </div>
        )}
      </div>
      
      {/* Subcategory Dropdown */}
      {selectedCategory && activeCategoryInfo?.subcategories?.length > 0 && (
        <div className="relative z-30">
          <button
            {...safeClick(() => {
              setShowSubcategoryDropdown(!showSubcategoryDropdown);
              setShowCategoryDropdown(false);
            })}
            className={`flex items-center gap-2 ${height} px-4 min-w-[100px] lg:min-w-[140px] text-sm font-medium text-foreground bg-transparent hover:bg-secondary/50 transition-colors border-r border-border/40 touch-manipulation`}
          >
            <span className="hidden lg:inline max-w-[100px] truncate">
              {!selectedSubcategory 
                ? <span className="text-muted-foreground">{t('subcategory')}</span>
                : selectedSubcategory.startsWith('all-') 
                  ? t('all') 
                  : (() => {
                      const subObj = activeCategoryInfo?.subcategories?.find(s => 
                        (typeof s === 'object' ? s.id : s) === selectedSubcategory
                      );
                      if (subObj && typeof subObj === 'object' && subObj.name) {
                        return getSubcategoryTranslation(subObj.name, language);
                      }
                      return getSubcategoryTranslation(selectedSubcategory, language);
                    })()
              }
            </span>
            <span className="lg:hidden text-muted-foreground">Sub</span>
            <ChevronDown className={`w-3.5 h-3.5 text-muted-foreground transition-transform ${showSubcategoryDropdown ? 'rotate-180' : ''}`} />
          </button>
          
          {showSubcategoryDropdown && (
            <div className="absolute top-full left-0 mt-2 w-48 bg-card border border-border rounded-xl shadow-xl z-[70] py-2 max-h-64 overflow-y-auto">
              <button
                onClick={() => {
                  setSelectedSubcategory(`all-${selectedCategory}`);
                  setShowSubcategoryDropdown(false);
                  const params = new URLSearchParams();
                  params.set('category', selectedCategory);
                  params.set('subcategory', `all-${selectedCategory}`);
                  if (searchQuery.trim()) params.set('search', searchQuery.trim());
                  navigate(`/marketplace?${params.toString()}`);
                }}
                className={`w-full px-4 py-2.5 text-left text-sm transition-colors ${
                  selectedSubcategory?.startsWith('all-') ? 'bg-primary/10 text-primary' : 'text-foreground hover:bg-secondary'
                }`}
              >
                {t('all')} {activeCategoryInfo.name}
              </button>
              {activeCategoryInfo.subcategories.map((sub) => {
                const subId = typeof sub === 'object' ? sub.id : sub;
                const subName = typeof sub === 'object' ? sub.name : sub;
                return (
                  <button
                    key={subId}
                    onClick={() => {
                      setSelectedSubcategory(subId);
                      setShowSubcategoryDropdown(false);
                      const params = new URLSearchParams();
                      params.set('category', selectedCategory);
                      params.set('subcategory', subId);
                      if (searchQuery.trim()) params.set('search', searchQuery.trim());
                      navigate(`/marketplace?${params.toString()}`);
                    }}
                    className={`w-full px-4 py-2.5 text-left text-sm transition-colors ${
                      selectedSubcategory === subId ? 'bg-primary/10 text-primary' : 'text-foreground hover:bg-secondary'
                    }`}
                  >
                    <span>{getSubcategoryTranslation(subName, language)}</span>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}
      
      {/* Search Input */}
      <div className="relative flex-1 h-full bg-transparent transition-all duration-300 self-stretch flex items-center">
        {suggestionsLoading ? (
          <div className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4">
            <div className="w-4 h-4 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
          </div>
        ) : (
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground/80" />
        )}
        <input
          ref={searchInputRef}
          type="text"
          placeholder={t('search_marketplace')}
          value={searchQuery}
          onChange={(e) => {
            isTypingRef.current = true; // User is actively typing
            setSearchQuery(e.target.value);
          }}
          onFocus={() => {
            setSearchFocused(true);
            setShowCategoryDropdown(false);
            setShowSubcategoryDropdown(false);
            // Only show suggestions if user is typing, not on URL-synced focus
            if (searchQuery.length >= 2 && isTypingRef.current) {
              setShowSuggestions(true);
            }
          }}
          onBlur={() => setTimeout(() => setSearchFocused(false), 200)}
          onKeyDown={handleSearchKeyDown}
          className="w-full h-full pl-11 pr-10 text-sm bg-transparent text-foreground placeholder:text-muted-foreground focus:outline-none"
          data-testid="searchbar-input"
          autoComplete="off"
        />
        
        {searchQuery && (
          <button
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setSearchQuery('');
              setShowSuggestions(false);
              if (location.pathname === '/marketplace') {
                const params = new URLSearchParams(location.search);
                params.delete('search');
                params.delete('q');
                navigate(`/marketplace?${params.toString()}`, { replace: true });
              }
            }}
            className="absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded-full hover:bg-secondary transition-colors"
          >
            <X className="w-3.5 h-3.5 text-muted-foreground" />
          </button>
        )}
        
        {/* Recent Searches Dropdown */}
        {searchFocused && recentSearches.length > 0 && !searchQuery && (
          <div className="absolute top-full left-0 right-0 mt-2 bg-card border border-border rounded-xl shadow-xl z-[80] overflow-hidden">
            <div className="flex items-center px-3 py-2 border-b border-border">
              <span className="text-xs font-medium text-muted-foreground flex items-center gap-1.5">
                <History className="w-3.5 h-3.5" />
                Recent
              </span>
            </div>
            <div className="py-1">
              {recentSearches.map((term, index) => (
                <button
                  key={index}
                  onClick={() => {
                    setSearchQuery(term);
                    const params = new URLSearchParams();
                    params.set('search', term);
                    if (selectedCategory) params.set('category', selectedCategory);
                    if (selectedSubcategory) params.set('subcategory', selectedSubcategory);
                    navigate(`/marketplace?${params.toString()}`);
                    setSearchFocused(false);
                    setShowSuggestions(false);
                    searchInputRef.current?.blur();
                  }}
                  className="w-full px-3 py-2 text-left text-sm text-foreground hover:bg-secondary/50 flex items-center gap-2 transition-colors"
                >
                  <Clock className="w-3.5 h-3.5 text-muted-foreground" />
                  <span className="truncate">{term}</span>
                </button>
              ))}
            </div>
          </div>
        )}
        
        {/* Autocomplete Suggestions */}
        {showSuggestions && searchQuery.length >= 2 && (suggestions.length > 0 || suggestedCategories.length > 0) && (
          <div 
            ref={suggestionsRef}
            className="absolute top-full left-0 right-0 mt-2 bg-card border border-border rounded-xl shadow-xl z-[80] overflow-hidden max-h-[400px] overflow-y-auto"
          >
            {suggestions.length > 0 && (
              <div className="p-2">
                <div className="px-2 py-1.5 text-xs font-medium text-muted-foreground uppercase tracking-wide">
                  Advertenties
                </div>
                {suggestions.map((suggestion, index) => (
                  <button
                    key={suggestion.id}
                    onClick={() => handleSuggestionClick(suggestion)}
                    className={`w-full flex items-center gap-3 px-2 py-2 rounded-lg text-left transition-colors ${
                      selectedSuggestionIndex === index 
                        ? 'bg-primary/10 text-primary' 
                        : 'hover:bg-secondary/50'
                    }`}
                  >
                    <div className="w-10 h-10 rounded-lg bg-secondary overflow-hidden flex-shrink-0">
                      {suggestion.image ? (
                        <img src={suggestion.image} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-muted-foreground">
                          <Package className="w-5 h-5" />
                        </div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-foreground truncate">
                        {highlightMatch(suggestion.title, searchQuery)}
                      </div>
                      <div className="text-xs text-muted-foreground flex items-center gap-2">
                        <span className="capitalize">{suggestion.category?.replace(/-/g, ' ')}</span>
                        {suggestion.price && (
                          <>
                            <span>•</span>
                            <span className="text-primary font-medium">
                              {suggestion.currency === 'XCG' ? '' : '$'}{suggestion.price?.toLocaleString()} {suggestion.currency === 'XCG' ? 'XCG' : ''}
                            </span>
                          </>
                        )}
                      </div>
                    </div>
                    <ChevronRight className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                  </button>
                ))}
              </div>
            )}
            
            {suggestedCategories.length > 0 && (
              <div className={`p-2 ${suggestions.length > 0 ? 'border-t border-border' : ''}`}>
                <div className="px-2 py-1.5 text-xs font-medium text-muted-foreground uppercase tracking-wide">
                  Zoek in categorie
                </div>
                {suggestedCategories.map((cat, index) => {
                  const catIndex = suggestions.length + index;
                  const SugCatIcon = getIconComponent(cat.icon);
                  return (
                    <button
                      key={cat.id}
                      onClick={() => handleCategorySuggestionClick(cat.id)}
                      className={`w-full flex items-center gap-3 px-2 py-2 rounded-lg text-left transition-colors ${
                        selectedSuggestionIndex === catIndex 
                          ? 'bg-primary/10 text-primary' 
                          : 'hover:bg-secondary/50'
                      }`}
                    >
                      <div className="w-8 h-8 rounded-lg bg-secondary flex items-center justify-center">
                        <SugCatIcon className="w-4 h-4" />
                      </div>
                      <div className="flex-1">
                        <span className="text-sm text-foreground">
                          Zoek "<span className="font-medium">{searchQuery}</span>" in{' '}
                          <span className="font-medium text-primary">{cat.name}</span>
                        </span>
                        <span className="text-xs text-muted-foreground ml-2">
                          ({cat.match_count} resultaten)
                        </span>
                      </div>
                      <ChevronRight className="w-4 h-4 text-muted-foreground" />
                    </button>
                  );
                })}
              </div>
            )}
            
            <div className="px-4 py-2 bg-secondary/30 border-t border-border text-xs text-muted-foreground flex items-center justify-between">
              <span>
                <kbd className="px-1.5 py-0.5 bg-secondary rounded text-[10px] font-mono">↑</kbd>
                <kbd className="px-1.5 py-0.5 bg-secondary rounded text-[10px] font-mono ml-1">↓</kbd>
                <span className="ml-1.5">navigeren</span>
              </span>
              <span>
                <kbd className="px-1.5 py-0.5 bg-secondary rounded text-[10px] font-mono">Enter</kbd>
                <span className="ml-1.5">selecteren</span>
              </span>
            </div>
          </div>
        )}
      </div>
      
      {/* Search Button */}
      <button 
        onClick={handleSearch}
        className={`${height} px-6 bg-primary text-primary-foreground font-semibold text-sm hover:bg-primary/90 transition-colors rounded-r-full flex items-center gap-2`}
        data-testid="searchbar-submit"
      >
        <Search className="w-4 h-4" />
        <span className="hidden lg:inline">{t('search')}</span>
      </button>
    </div>
  );
};

export default SearchBar;
